import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {Button, Heading, Popover, Stack} from '@primer/react'
import {KeybindingHint} from '@primer/react/experimental'
import {useCallback, useEffect} from 'react'

import {getInitialState} from '../helpers/initial-state'
import {useAutoAddItemCount} from '../state-providers/auto-add-item-count/auto-add-item-count-provider'
import {useProjectDetails} from '../state-providers/memex/use-project-details'
import {useMemexItems} from '../state-providers/memex-items/use-memex-items'
import styles from './onboarding-popover.module.css'

const storageKeyPrefix = 'projects.shouldShowOnboardingPopover'

const getOnboardingPopoverStorageKey = (projectId?: number) => {
  if (projectId) {
    return `${storageKeyPrefix}.${projectId}`
  } else {
    return storageKeyPrefix
  }
}

export const useOnboardingPopover = () => {
  const {projectData} = getInitialState()
  const projectId = projectData?.id

  const storageKey = getOnboardingPopoverStorageKey(projectId)
  const [shouldShowOnboardingPopover, setShouldShowOnboardingPopover] = useLocalStorage(storageKey, false)
  const {defaultIssueCreateRepository} = useProjectDetails()
  const {autoAddItemCount} = useAutoAddItemCount()

  const dismiss = useCallback(() => {
    setShouldShowOnboardingPopover(false)
  }, [setShouldShowOnboardingPopover])

  // Automatically dismiss if repository is attached AND has auto-add items
  useEffect(() => {
    if (defaultIssueCreateRepository !== null && autoAddItemCount > 0) {
      dismiss()
    }
  }, [defaultIssueCreateRepository, autoAddItemCount, dismiss, shouldShowOnboardingPopover])

  return {
    shouldShowOnboardingPopover: shouldShowOnboardingPopover && autoAddItemCount === 0,
    setShouldShowOnboardingPopover,
    dismiss,
  }
}

export const OnboardingPopover = ({
  addItem,
  onDismiss,
}: {
  addItem: (e: React.MouseEvent) => void
  onDismiss: () => void
}) => {
  const {items} = useMemexItems()
  const hasItems = items.length > 0

  const {dismiss} = useOnboardingPopover()

  const handleDismiss = () => {
    dismiss()
    onDismiss?.()
  }

  const text = hasItems ? 'Add more items' : 'Add your first item'
  return (
    <Popover relative={false} open caret="top-left" className={styles.onboardingPopover}>
      <Popover.Content className={styles.popoverContent}>
        <Stack direction="vertical" gap="condensed">
          <Heading as="h3" className={styles.popoverHeading}>
            {text}
          </Heading>
          <p className={styles.popoverBody}>
            Click &quot;Add Item&quot; to get started or use the shortcut{' '}
            <KeybindingHint format="full" keys="Control+Space" />.
          </p>
          <Stack direction="horizontal" className={styles.actionButtonStack}>
            <Button onClick={addItem}>Add item</Button>
            <Button variant="invisible" onClick={handleDismiss}>
              OK, dismiss
            </Button>
          </Stack>
        </Stack>
      </Popover.Content>
    </Popover>
  )
}
